import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Lock, ClipboardPaste, Eye, Trash2, Pencil } from 'lucide-react';
import { ScreenshotQuestionModal } from './ScreenshotQuestionModal';
import { StagedPreviewModal } from './StagedPreviewModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StagedQuestionsPanel = ({ token, isAr }) => {
  const [items, setItems] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [source, setSource] = useState(null);
  const [free, setFree] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API_URL}/api/admin/staged-questions`, { headers: { Authorization: `Bearer ${token}` } });
    setItems(data.questions);
    const src = await axios.get(`${API_URL}/api/admin/b-question-source`, { headers: { Authorization: `Bearer ${token}` } });
    setSource(src.data);
    const fr = await axios.get(`${API_URL}/api/admin/free-test-selection`, { headers: { Authorization: `Bearer ${token}` } });
    setFree(fr.data);
  }, [token]);

  const toggleFree = async (key, id) => {
    const cur = free[key]?.manual ? free[key].ids : [];
    const ids = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    try {
      const { data } = await axios.put(`${API_URL}/api/admin/free-test-selection`, { test: key, ids }, { headers });
      setFree(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const toggleSource = async () => {
    const next = source?.source === 'staged' ? 'live' : 'staged';
    const msg = next === 'staged'
      ? (isAr ? 'ستأخذ كل اختبارات B أسئلتها من القسم الخاص. متابعة؟' : 'Alla B-prov hämtar frågor från dolda frågor. Fortsätt?')
      : (isAr ? 'ستعود اختبارات B للأسئلة القديمة. متابعة؟' : 'B-proven går tillbaka till de gamla frågorna. Fortsätt?');
    if (!window.confirm(msg)) return;
    const { data } = await axios.put(`${API_URL}/api/admin/b-question-source`, { source: next }, { headers });
    setSource(data);
    toast.success(isAr ? 'تم التغيير' : 'Ändrat');
  };
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm(isAr ? 'حذف هذا السؤال من القسم الخاص؟' : 'Radera frågan?')) return;
    await axios.delete(`${API_URL}/api/admin/staged-questions/${id}`, { headers });
    toast.success(isAr ? 'تم الحذف' : 'Raderad');
    load();
  };

  return (
    <div className="space-y-4" data-testid="staged-panel">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <Lock className="w-5 h-5 text-slate-500" />
        <p className="text-sm text-slate-600 flex-1 min-w-[200px]">
          {isAr ? 'القسم الخاص: الأسئلة لا تظهر في بنك الأسئلة العام ولا تُترجم تلقائياً.' : 'Dolda frågor: visas inte i den vanliga frågebanken och översätts inte automatiskt.'}
        </p>
        <Button onClick={() => { setEditing(null); setAddOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-question-from-images-btn">
          <ClipboardPaste className="w-4 h-4 me-2" />{isAr ? 'إضافة بلصق النص' : 'Klistra in fråga'}
        </Button>
      </div>
      {source && (
        <div className={`flex flex-wrap items-center gap-3 rounded-xl border p-4 ${source.active ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="b-source-box">
          <span className={`h-2.5 w-2.5 rounded-full ${source.active ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          <p className="text-sm font-semibold flex-1 min-w-[200px]" data-testid="b-source-status">
            {source.active
              ? (isAr ? `اختبارات B تستخدم الآن أسئلة القسم الخاص (${source.staged_count} سؤال)` : `B-proven använder nu dolda frågor (${source.staged_count})`)
              : source.source === 'staged'
                ? (isAr ? 'القسم الخاص فارغ، لذلك تستخدم الاختبارات الأسئلة القديمة مؤقتاً' : 'Dolda frågor är tomma – proven använder de gamla frågorna tills vidare')
                : (isAr ? 'اختبارات B تستخدم الآن الأسئلة القديمة' : 'B-proven använder nu de gamla frågorna')}
          </p>
          <Button size="sm" variant="outline" onClick={toggleSource} data-testid="b-source-toggle">
            {source.source === 'staged' ? (isAr ? 'رجوع للأسئلة القديمة' : 'Använd gamla frågor') : (isAr ? 'استخدام القسم الخاص' : 'Använd dolda frågor')}
          </Button>
        </div>
      )}
      {Object.keys(free).length > 0 && (
        <div className="flex flex-wrap gap-3" data-testid="free-selection-summary">
          {['free1', 'free2'].filter((k) => free[k]).map((k, n) => (
            <div key={k} className="flex-1 min-w-[220px] rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm" data-testid={`free-summary-${k}`}>
              <p className="font-bold text-sky-800">{isAr ? `الاختبار المجاني ${n + 1}` : `Gratisprov ${n + 1}`} — {free[k].title}</p>
              <p className="text-sky-700" data-testid={`free-count-${k}`}>
                {free[k].ids.length} / {free[k].count} {isAr ? 'سؤال' : 'frågor'}
                {!free[k].manual && free[k].ids.length > 0 && (isAr ? ' (اختيار تلقائي)' : ' (automatiskt)')}
              </p>
              <p className="text-xs text-sky-600 mt-1">{isAr ? 'اضغط زر الرقم بجانب أي سؤال لإضافته أو إزالته. الترتيب: صورة ثم بدون صورة.' : 'Klicka på siffran vid en fråga för att lägga till/ta bort.'}</p>
            </div>
          ))}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-center text-sm text-slate-400 py-10" data-testid="staged-empty">{isAr ? 'لا توجد أسئلة بعد' : 'Inga frågor ännu'}</p>
      ) : (
        <ul className="divide-y rounded-xl border bg-white" data-testid="staged-list">
          {items.map((q, i) => (
            <li key={q.id} className="flex flex-wrap items-center gap-2 p-3" data-testid={`staged-row-${q.id}`}>
              <span className="text-xs font-mono text-slate-400 w-8">{items.length - i}</span>
              {q.image_url && <img src={q.image_url.startsWith('http') ? q.image_url : `${API_URL}${q.image_url}`} alt="" className="h-10 w-10 rounded object-cover border" />}
              <div className="flex-1 min-w-0 basis-[calc(100%-4.5rem)] sm:basis-0">
                <p className="text-sm font-medium truncate">{q.question?.sv}</p>
                <p className="text-xs text-slate-400">{Object.keys(q.question || {}).filter((k) => q.question[k]).map((k) => k.toUpperCase()).join(' · ')} — {q.licenseType === 'taxi' ? 'Taxi' : 'B'}</p>
              </div>
              {['free1', 'free2'].filter((k) => free[k]).map((k, n) => {
                const on = free[k].ids.includes(q.id);
                return (
                  <button key={k} type="button" onClick={() => toggleFree(k, q.id)} title={isAr ? `الاختبار المجاني ${n + 1}` : `Gratisprov ${n + 1}`}
                    className={`h-8 min-w-[2.75rem] rounded-full border px-2 text-xs font-bold transition-colors ${on ? 'bg-sky-600 text-white border-sky-600' : 'border-sky-200 text-sky-700 hover:bg-sky-50'}`}
                    data-testid={`free-toggle-${k}-${q.id}`}>{on ? '✓ ' : ''}{isAr ? `م${n + 1}` : `G${n + 1}`}</button>
                );
              })}
              <Button size="sm" variant="outline" onClick={() => { setEditing(q); setAddOpen(true); }} data-testid={`staged-edit-${q.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => setPreview(q)} data-testid={`staged-preview-${q.id}`}><Eye className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(q.id)} className="text-rose-600" data-testid={`staged-delete-${q.id}`}><Trash2 className="w-4 h-4" /></Button>
            </li>
          ))}
        </ul>
      )}
      <ScreenshotQuestionModal open={addOpen} initial={editing} onClose={() => { setAddOpen(false); setEditing(null); }} token={token} onSuccess={load} />
      <StagedPreviewModal question={preview} onClose={() => setPreview(null)} />
    </div>
  );
};
